import { ref, reactive } from "vue";

export interface Notification {
	id: string;
	message: string;
	type: "success" | "error" | "warning" | "info";
	timeout?: number;
	actions?: Array<{
		text: string;
		action: () => void;
	}>;
}

const notifications = ref<Notification[]>([]);
let notificationId = 0;

export const useNotification = () => {
	const show = (notification: Omit<Notification, "id">) => {
		// Deduplicação: se já existe notificação com mesma mensagem e tipo, ignora
		const duplicate = notifications.value.find(
			(n) => n.message === notification.message && n.type === notification.type
		);
		if (duplicate) {
			return duplicate.id;
		}

		const id = `notification_${++notificationId}`;
		const newNotification: Notification = {
			id,
			timeout: 5000, // 5 segundos por padrão
			...notification,
		};

		notifications.value.push(newNotification);

		// Auto remove notification after timeout
		if (newNotification.timeout && newNotification.timeout > 0) {
			setTimeout(() => {
				remove(id);
			}, newNotification.timeout);
		}

		return id;
	};

	const remove = (id: string) => {
		const index = notifications.value.findIndex((n) => n.id === id);
		if (index > -1) {
			notifications.value.splice(index, 1);
		}
	};

	const clear = () => {
		notifications.value.splice(0);
	};

	// Métodos de conveniência
	const success = (
		message: string,
		options?: Partial<Omit<Notification, "id" | "message" | "type">>
	) => {
		return show({ message, type: "success", ...options });
	};

	const error = (
		message: string,
		options?: Partial<Omit<Notification, "id" | "message" | "type">>
	) => {
		return show({ message, type: "error", timeout: 8000, ...options }); // Erros ficam mais tempo
	};

	const warning = (
		message: string,
		options?: Partial<Omit<Notification, "id" | "message" | "type">>
	) => {
		return show({ message, type: "warning", timeout: 6000, ...options });
	};

	const info = (
		message: string,
		options?: Partial<Omit<Notification, "id" | "message" | "type">>
	) => {
		return show({ message, type: "info", ...options });
	};

	return {
		notifications,
		show,
		remove,
		clear,
		success,
		error,
		warning,
		info,
	};
};
